import React, { useCallback, useRef, useState } from 'react';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import useUserSettingsStore, { TableSetting, TABLE_SETTING_VERSION } from '../store/userSettingsStore';
import ResizableHeaderCell from '../components/TableColumnSetting/ResizableHeaderCell';

export type FixedSide = 'left' | 'right';

/**
 * Cột dùng được với cài đặt bảng. Cần `key` hoặc `dataIndex` cố định (không đổi giữa các bản build).
 * - settingLabel: nhãn hiển thị trong popover khi `title` là JSX / hàm
 * - lockVisible:  cột luôn hiện, không cho ẩn (vd: Thao tác)
 * - resizable:    false để tắt kéo đổi độ rộng cho riêng cột này
 * - defaultHidden: cột ẩn sẵn, người dùng tự bật trong cài đặt cột. Chỉ áp dụng khi cài đặt
 *                  đã lưu chưa biết tới cột này (cột mới thêm), không đè lựa chọn đã lưu.
 */
export type SettingColumn<T> = ColumnType<T> & {
  settingLabel?: React.ReactNode;
  lockVisible?: boolean;
  resizable?: boolean;
  defaultHidden?: boolean;
};

export interface ColumnSettingItem {
  key: string;
  label: React.ReactNode;
  visible: boolean;
  locked: boolean;
  fixed?: FixedSide;
  /** Vị trí khai báo trong code — dùng sắp xếp nhóm cột đang ẩn */
  defaultIndex: number;
}

export interface ColumnSettingProps {
  items: ColumnSettingItem[];
  hasCustomWidths: boolean;
  onToggle: (key: string) => void;
  onToggleAll: (checked: boolean) => void;
  onMove: (activeKey: string, overKey: string) => void;
  onFixedChange: (key: string, side?: FixedSide) => void;
  onReset: () => void;
  onResetWidths: () => void;
}

const MIN_WIDTH = 60;
/** Độ rộng cho cột không khai báo width khi bảng đã chuyển sang layout fixed */
const DEFAULT_WIDTH = 160;

const defaultFixedOf = (col: ColumnType<any>): FixedSide | undefined =>
  col.fixed === true ? 'left' : col.fixed || undefined;

/** Key cố định của cột: `key`, nếu không có thì lấy `dataIndex` */
const keyOf = (col: ColumnType<any>): string => {
  if (col.key !== undefined && col.key !== null) return String(col.key);
  const di = col.dataIndex;
  if (Array.isArray(di)) return di.join('.');
  if (di !== undefined && di !== null) return String(di);
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[useTableColumnSettings] Cột thiếu key/dataIndex, cài đặt có thể không giữ được:', col.title);
  }
  return String(col.title);
};

// PHP json_encode trả object rỗng {} thành mảng [] -> đưa về object
const asMap = <V,>(v: unknown): Record<string, V> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, V>) : {};

/**
 * Ẩn/hiện, sắp xếp, cố định trái/phải và kéo đổi độ rộng cột cho antd Table.
 * Cài đặt lưu theo tài khoản trong extra_data.table_settings[tableKey].
 *
 *   const { columns, components, tableLayout, scrollX, settingProps } =
 *       useTableColumnSettings('customer_list', cols, { extraWidth: 48 }); // 48 = rowSelection.columnWidth
 *   <ColumnSettingButton {...settingProps} />
 *   <Table columns={columns} components={components} tableLayout={tableLayout} scroll={{ x: scrollX }} ... />
 */
export default function useTableColumnSettings<T>(
  tableKey: string,
  sourceColumns: ColumnsType<T>,
  options: {
    /** Tổng độ rộng các cột hook không quản lý (checkbox chọn dòng, expand...) — cần khai báo columnWidth tương ứng */
    extraWidth?: number;
    /** scroll.x khi chưa chỉnh độ rộng (mặc định 'max-content') */
    scrollX?: number | string | true;
  } = {},
) {
  const rawSetting = useUserSettingsStore((s) => s.extraData?.table_settings?.[tableKey]);
  const setTableSetting = useUserSettingsStore((s) => s.setTableSetting);
  const saved: TableSetting | null =
    rawSetting && rawSetting.v === TABLE_SETTING_VERSION ? rawSetting : null;

  // Độ rộng tạm trong lúc đang kéo (chưa lưu) để bảng giãn / co theo tay kéo
  const [dragWidths, setDragWidths] = useState<Record<string, number> | null>(null);
  const rafRef = useRef<number | null>(null);

  const columns = sourceColumns as SettingColumn<T>[];
  const colMap = new Map(columns.map((c) => [keyOf(c), c]));
  const defaultKeys = columns.map(keyOf);

  // Thứ tự: theo cài đặt đã lưu, cột mới (chưa có trong cài đặt) chèn sau cột đứng trước nó mặc định
  const order = (saved?.order || []).filter((k) => colMap.has(k));
  defaultKeys.forEach((k, i) => {
    if (order.includes(k)) return;
    const prev = defaultKeys.slice(0, i).reverse().find((p) => order.includes(p));
    order.splice(prev ? order.indexOf(prev) + 1 : 0, 0, k);
  });

  const hidden = new Set(saved?.hidden || []);
  // Cột có trong cài đặt đã lưu -> theo lựa chọn đã lưu; cột chưa có -> theo defaultHidden
  const knownKeys = new Set(saved?.order || []);
  const savedFixed = asMap<'left' | 'right' | 'none'>(saved?.fixed);
  const fixedOf = (key: string): FixedSide | undefined => {
    const override = savedFixed[key];
    if (override === 'none') return undefined;
    return override || defaultFixedOf(colMap.get(key)!);
  };

  const toItem = (key: string): ColumnSettingItem => {
    const col = colMap.get(key)!;
    const locked = !!col.lockVisible;
    return {
      key,
      label: col.settingLabel ?? (typeof col.title === 'function' ? key : (col.title as React.ReactNode)),
      locked,
      visible: locked || (knownKeys.has(key) ? !hidden.has(key) : !col.defaultHidden),
      fixed: fixedOf(key),
      defaultIndex: defaultKeys.indexOf(key),
    };
  };
  const all = order.map(toItem);
  // antd yêu cầu cột cố định trái ở đầu, cố định phải ở cuối
  const items = [
    ...all.filter((i) => i.fixed === 'left'),
    ...all.filter((i) => !i.fixed),
    ...all.filter((i) => i.fixed === 'right'),
  ];

  const savedWidths = asMap<number>(saved?.widths);

  /* ---------- lưu ---------- */

  const persist = (next: {
    items?: ColumnSettingItem[];
    widths?: Record<string, number>;
  }) => {
    const list = next.items || items;
    const fixed: TableSetting['fixed'] = {};
    list.forEach((i) => {
      const def = defaultFixedOf(colMap.get(i.key)!);
      if (i.fixed !== def) fixed[i.key] = i.fixed || 'none';
    });
    setTableSetting(tableKey, {
      v: TABLE_SETTING_VERSION,
      order: list.map((i) => i.key),
      hidden: list.filter((i) => !i.visible).map((i) => i.key),
      fixed,
      widths: next.widths || savedWidths,
    });
  };

  /**
   * Cột vừa bật hiện lại ở vị trí mặc định: ngay sau cột đang hiện gần nhất đứng trước nó
   * trong code (cùng nhóm cố định). Không có thì đứng đầu nhóm của nó.
   */
  const placeAtDefault = (list: ColumnSettingItem[], key: string) => {
    const item = list.find((i) => i.key === key);
    if (!item) return list;
    const rest = list.filter((i) => i.key !== key);
    const sameGroup = (i: ColumnSettingItem) => i.visible && i.fixed === item.fixed;
    const prev = defaultKeys
      .slice(0, item.defaultIndex)
      .reverse()
      .map((k) => rest.find((i) => i.key === k))
      .find((i): i is ColumnSettingItem => !!i && sameGroup(i));

    let at: number;
    if (prev) at = rest.indexOf(prev) + 1;
    else {
      const firstSame = rest.findIndex(sameGroup);
      if (firstSame >= 0) at = firstSame;
      else if (item.fixed === 'left') at = 0;
      else if (item.fixed === 'right') at = rest.length;
      else at = rest.filter((i) => i.fixed === 'left').length;
    }
    rest.splice(at, 0, item);
    return rest;
  };

  const onToggle = (key: string) => {
    const current = items.find((i) => i.key === key);
    if (!current || current.locked) return;
    let next = items.map((i) => (i.key === key ? { ...i, visible: !i.visible } : i));
    if (!next.some((i) => i.visible)) return; // luôn giữ ít nhất 1 cột
    if (!current.visible) next = placeAtDefault(next, key);
    persist({ items: next });
  };

  const onToggleAll = (checked: boolean) => {
    if (checked) {
      // Bật lần lượt theo thứ tự trong code để các cột về đúng vị trí mặc định
      let next = items;
      items
        .filter((i) => !i.visible)
        .sort((a, b) => a.defaultIndex - b.defaultIndex)
        .forEach(({ key }) => {
          next = placeAtDefault(next.map((i) => (i.key === key ? { ...i, visible: true } : i)), key);
        });
      persist({ items: next });
      return;
    }
    const next = items.map((i) => ({ ...i, visible: i.locked }));
    // Ẩn tất cả nhưng không có cột khoá -> giữ lại cột đầu tiên
    if (!next.some((i) => i.visible) && next.length) next[0] = { ...next[0], visible: true };
    persist({ items: next });
  };

  const onMove = (activeKey: string, overKey: string) => {
    const from = items.findIndex((i) => i.key === activeKey);
    const to = items.findIndex((i) => i.key === overKey);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persist({ items: next });
  };

  const onFixedChange = (key: string, side?: FixedSide) => {
    const current = items.find((i) => i.key === key);
    if (!current || current.fixed === side) return;
    // Đặt cột vào sát ranh giới nhóm mới: gần vị trí cũ nhất trên bảng
    const rest = items.filter((i) => i.key !== key);
    const leftEnd = rest.filter((i) => i.fixed === 'left').length;
    const rightStart = rest.length - rest.filter((i) => i.fixed === 'right').length;
    let insertAt: number;
    if (side === 'left') insertAt = leftEnd;
    else if (side === 'right') insertAt = rightStart;
    else insertAt = current.fixed === 'left' ? leftEnd : rightStart;
    rest.splice(insertAt, 0, { ...current, fixed: side });
    persist({ items: rest });
  };

  const onReset = () => {
    setDragWidths(null);
    setTableSetting(tableKey, null);
  };

  const onResetWidths = () => {
    setDragWidths(null);
    persist({ widths: {} });
  };

  /* ---------- kéo đổi độ rộng ---------- */
  // table-layout auto luôn giãn cột theo nội dung nên không thu hẹp được.
  // Khi bắt đầu kéo: chốt độ rộng thực tế của mọi cột và chuyển sang layout fixed.

  const handleResizeStart = useCallback((measured: Record<string, number>) => {
    setDragWidths(measured);
  }, []);

  const handleResize = useCallback((key: string, width: number) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setDragWidths((w) => ({ ...(w || {}), [key]: width }));
    });
  }, []);

  const handleResizeEnd = (key: string, width: number, measured: Record<string, number>) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setDragWidths(null);
    persist({ widths: { ...savedWidths, ...measured, [key]: Math.round(width) } });
  };

  const layoutFixed = !!dragWidths || Object.keys(savedWidths).length > 0;

  /* ---------- cột cho Table ---------- */

  const viewColumns: ColumnsType<T> = items
    .filter((i) => i.visible)
    .map((i) => {
      const { settingLabel, lockVisible, resizable, defaultHidden, ...col } = colMap.get(i.key)!;
      const width =
        dragWidths?.[i.key] ?? savedWidths[i.key] ?? col.width ?? (layoutFixed ? DEFAULT_WIDTH : undefined);
      const canResize = resizable !== false;
      return {
        ...col,
        key: i.key,
        fixed: i.fixed,
        width,
        onHeaderCell: (column: Parameters<NonNullable<ColumnType<T>['onHeaderCell']>>[0]) => ({
          ...(col.onHeaderCell?.(column) || {}),
          ...(canResize
            ? {
                resizeKey: i.key,
                minWidth: MIN_WIDTH,
                onColResizeStart: handleResizeStart,
                onColResize: handleResize,
                onColResizeEnd: handleResizeEnd,
              }
            : {}),
        } as React.HTMLAttributes<any>),
      };
    });

  const settingProps: ColumnSettingProps = {
    items,
    hasCustomWidths: Object.keys(savedWidths).length > 0,
    onToggle,
    onToggleAll,
    onMove,
    onFixedChange,
    onReset,
    onResetWidths,
  };

  // width: max-content khiến trình duyệt bỏ qua table-layout fixed -> phải cho scroll.x là số cụ thể
  const scrollX: number | string | true = layoutFixed
    ? viewColumns.reduce((sum, c) => sum + (Number(c.width) || DEFAULT_WIDTH), options.extraWidth || 0)
    : options.scrollX ?? 'max-content';

  return {
    columns: viewColumns,
    components: { header: { cell: ResizableHeaderCell } },
    tableLayout: layoutFixed ? ('fixed' as const) : undefined,
    scrollX,
    settingProps,
  };
}
